import type React from "react";
import { useRef } from "react";

type FocusHandlers = {
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
  onCloseAutoFocus?: (event: Event) => void;
};

// Radix only returns focus to its own Trigger on close, so a modal opened from
// state (no Trigger) drops focus onto <body>. Remember where focus came from
// when it first entered the modal and send focus back there instead. This is
// read from the focus event rather than Radix's onOpenAutoFocus because Radix
// skips that event when content focuses itself on mount.
export const useReturnFocusToOpener = ({
  onFocus,
  onCloseAutoFocus,
}: FocusHandlers): Required<FocusHandlers> => {
  const openerRef = useRef<HTMLElement | null>(null);

  return {
    onFocus: (event) => {
      onFocus?.(event);

      const { relatedTarget, currentTarget } = event;

      if (openerRef.current) return;
      if (!(relatedTarget instanceof HTMLElement)) return;
      if (currentTarget.contains(relatedTarget)) return;

      openerRef.current = relatedTarget;
    },
    onCloseAutoFocus: (event) => {
      onCloseAutoFocus?.(event);

      const opener = openerRef.current;
      openerRef.current = null;

      if (event.defaultPrevented) return;
      if (!opener || !opener.isConnected) return;

      event.preventDefault();
      opener.focus();
    },
  };
};
